import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Drawer, IconButton, Menu, MenuItem } from "@outpave/ds";
import SectionTabs from "components/common/SectionTabs";
import PanelSection from "components/common/PanelSection";
import { Stack } from "components/layout/reusableLayouts";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Copy01Icon,
  EyeIcon,
  Payment02Icon,
  RepeatIcon,
  ImageAdd01Icon,
  MoreVerticalIcon,
  PlayCircleIcon,
  QrCode01Icon,
  Settings01Icon,
  StopCircleIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { toast } from "react-toastify";
import moment from "moment";
import RenderPaymentLinkStatus from "components/common/RenderPaymentLinkStatus";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { PaymentLinkStatus, PaymentLinkType } from "enums/PaymentLink";
import { PaymentLink } from "hooks/usePaymentLinkData";
import PaymentLinkCopyRow from "./PaymentLinkCopyRow";
import PaymentLinkStatTiles from "./PaymentLinkStatTiles";
import PaymentLinkPaymentRows from "./PaymentLinkPaymentRows";
import PaymentLinkQrDialog from "./PaymentLinkQrDialog";
import PaymentLinkSubscriberRows from "./PaymentLinkSubscriberRows";
import { intervalPeriod } from "utils/billingInterval";
import { describePaymentLinkAmount } from "utils/paymentLinkAmount";
import { previewUrl } from "utils/paymentLinkUrl";

interface PaymentLinkDetailsDrawerProps {
  open: boolean;
  paymentLink: PaymentLink | null;
  loading: boolean;
  readOnly: boolean;
  onClose: () => void;
  onEdit: (paymentLinkId: string) => void;
  onToggleStatus: (paymentLinkId: string) => void;
  onViewPayments: (paymentLinkId: string) => void;
  onViewSubscribers: (paymentLinkId: string) => void;
}

const PaymentLinkDetailsDrawer: React.FC<PaymentLinkDetailsDrawerProps> = ({
  open,
  paymentLink,
  loading,
  readOnly,
  onClose,
  onEdit,
  onToggleStatus,
  onViewPayments,
  onViewSubscribers,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [qrOpen, setQrOpen] = React.useState(false);

  const copyUrl = async () => {
    if (!paymentLink?.url) return;

    try {
      await navigator.clipboard.writeText(paymentLink.url);
      toast.success(t("Link copied"));
    } catch (error) {
      toast.error(t("Could not copy the link"));
    }
  };

  const isActive = paymentLink?.status === PaymentLinkStatus.ACTIVE;
  const isSubscription = paymentLink?.linkType === PaymentLinkType.SUBSCRIPTION;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      loading={loading}
      title={t("Payment Link")}
      headerAction={
        !readOnly && paymentLink ? (
          <>
            <IconButton
              variant="ghost"
              aria-label={t("More actions")}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              <HugeiconsIcon icon={MoreVerticalIcon} size={20} />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem
                leading={
                  <HugeiconsIcon
                    icon={isActive ? StopCircleIcon : PlayCircleIcon}
                    size={20}
                  />
                }
                onClick={() => {
                  setAnchorEl(null);
                  onToggleStatus(paymentLink.id);
                }}
              >
                {isActive ? t("Deactivate") : t("Reactivate")}
              </MenuItem>
            </Menu>
          </>
        ) : undefined
      }
      footer={
        !readOnly && paymentLink ? (
          <Button onClick={() => onEdit(paymentLink.id)}>
            {t("Edit link")}
          </Button>
        ) : undefined
      }
    >
      {paymentLink && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-6)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-1)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "var(--space-3)",
                }}
              >
                <Typography variant="h6">{paymentLink.name}</Typography>
                <RenderPaymentLinkStatus
                  status={paymentLink.status}
                  expiresAt={paymentLink.expiresAt}
                />
              </Box>

              {paymentLink.description && (
                <Typography variant="body2" color="var(--color-text-secondary)">
                  {paymentLink.description}
                </Typography>
              )}
            </Box>

            <Typography variant="h4">
              {describePaymentLinkAmount(paymentLink, t)}
            </Typography>

            <Typography variant="caption" color="var(--color-text-tertiary)">
              {describeOrigin(paymentLink, t)}
            </Typography>
          </Box>

          <PaymentLinkCopyRow url={paymentLink.url} onCopy={copyUrl} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "var(--space-3)",
            }}
          >
            <Button
              startIcon={<HugeiconsIcon icon={Copy01Icon} size={18} />}
              onClick={copyUrl}
            >
              {t("Copy link")}
            </Button>
            <Button
              variant="default-outlined"
              startIcon={<HugeiconsIcon icon={QrCode01Icon} size={18} />}
              onClick={() => setQrOpen(true)}
            >
              {t("QR Code")}
            </Button>
            <Button
              variant="default-outlined"
              startIcon={<HugeiconsIcon icon={EyeIcon} size={18} />}
              onClick={() =>
                window.open(
                  previewUrl(paymentLink),
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              {t("Preview")}
            </Button>
          </Box>

          <PaymentLinkStatTiles paymentLink={paymentLink} />

          <SectionTabs
            ariaLabel={t("Payment link sections")}
            tabs={[
              // Enrollment creates a subscription rather than a payment row.
              isSubscription
                ? {
                    key: "activity",
                    label: t("Subscribers"),
                    icon: <HugeiconsIcon icon={RepeatIcon} />,
                    panel: (
                      <PaymentLinkSubscriberRows
                        paymentLinkId={paymentLink.id}
                        onViewAll={() => onViewSubscribers(paymentLink.id)}
                      />
                    ),
                  }
                : {
                    key: "activity",
                    label: t("Payments"),
                    icon: <HugeiconsIcon icon={Payment02Icon} />,
                    panel: (
                      <PaymentLinkPaymentRows
                        paymentLinkId={paymentLink.id}
                        onViewAll={() => onViewPayments(paymentLink.id)}
                      />
                    ),
                  },
              {
                key: "settings",
                label: t("Settings"),
                icon: <HugeiconsIcon icon={Settings01Icon} />,
                panel: (
                  <Stack gap="var(--space-5)">
                    <Stack gap="var(--space-3)">
                      {settingsRows(paymentLink, t).map((row) => (
                        <Box
                          key={row.label}
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: "var(--space-4)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="var(--color-text-tertiary)"
                          >
                            {row.label}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{ textAlign: "right" }}
                          >
                            {row.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>

                    <PanelSection
                      title={t("Attachments")}
                      icon={<HugeiconsIcon icon={ImageAdd01Icon} />}
                    >
                      <AttachmentsComponent
                        renderType={AttachmentRenderType.ACCORDION}
                        drawerType={AttachmentDrawerType.ATTACHMENT}
                        parentDetails={{
                          parentId: paymentLink.id,
                          parentType: ATTACHMENT_PARENT_TYPE.PAYMENT_LINK,
                        }}
                        dragAndDropHint={t("paymentLink.AttachmentsHint")}
                        readOnly={readOnly}
                      />
                    </PanelSection>
                  </Stack>
                ),
              },
            ]}
          />

          <PaymentLinkQrDialog
            open={qrOpen}
            url={paymentLink.url}
            fileName={paymentLink.slug}
            onClose={() => setQrOpen(false)}
          />
        </Box>
      )}
    </Drawer>
  );
};

function settingsRows(
  paymentLink: PaymentLink,
  t: (key: string, options?: Record<string, unknown>) => string,
): { label: string; value: string }[] {
  return [
    {
      label: t("Expiry"),
      value: paymentLink.expiresAt
        ? moment(paymentLink.expiresAt).format("ll")
        : t("No expiry"),
    },
    {
      label: t("Fee donation"),
      value: paymentLink.feeCover?.enabled ? t("On") : t("Off"),
    },
    {
      label: t("Bank payment discount"),
      value: paymentLink.cashDiscount?.enabled ? t("On") : t("Off"),
    },
    {
      label: t("After payment"),
      value: describeSuccess(paymentLink, t),
    },
    {
      label: t("On their statement"),
      value: paymentLink.statementDescriptor || "",
    },
  ].filter((row) => !!row.value);
}

function describeSuccess(
  paymentLink: PaymentLink,
  t: (key: string) => string,
): string {
  return paymentLink.successBehavior?.redirectUrl
    ? t("Redirect to a page")
    : t("Show a message");
}

function describeOrigin(
  paymentLink: PaymentLink,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const cadence = paymentLink.maxPayments
    ? t("EveryIntervalForCount", {
        interval: t(intervalPeriod(paymentLink.interval)),
        count: paymentLink.maxPayments,
      })
    : paymentLink.interval
      ? t("EveryInterval", {
          interval: t(intervalPeriod(paymentLink.interval)),
        })
      : t("One time");

  const parts = [cadence, (paymentLink.currency || "").toUpperCase()];
  if (paymentLink.createdAt) {
    parts.push(
      t("CreatedOn", { date: moment(paymentLink.createdAt).format("ll") }),
    );
  }

  return parts.filter(Boolean).join(" · ");
}

export default PaymentLinkDetailsDrawer;
